/**
 * Founder Interview — Admin-only page where Michael trains Excelior.
 *
 * Captures Michael's voice across 39 structured questions, organised
 * by category. Excelior's chat endpoint layers these answers into its
 * system prompt so it speaks in Michael's voice.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Save, Trash2, Loader2, Sparkles, Mic, Square } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem("reclaim_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const CATEGORY_META = {
  origin:     { label: "Origin & Identity",  hint: "Who you are, why Reclaim exists.",            tint: "#CBA153" },
  philosophy: { label: "Philosophy",          hint: "Your core beliefs about state and the work.", tint: "#8B92F2" },
  voice:      { label: "Tone & Voice",        hint: "How you actually speak.",                    tint: "#E5DFCE" },
  framework:  { label: "Coaching Framework",  hint: "Common situations, your typical response.",  tint: "#9DCBA8" },
  app:        { label: "App Mapping",         hint: "When users should go where.",                tint: "#D8A78F" },
  boundaries: { label: "Boundaries",          hint: "What Excelior should not do.",               tint: "#C99B7E" },
  personal:   { label: "Personal",            hint: "Your routine, your stories.",                tint: "#B7D2D4" },
};
const CATEGORY_ORDER = ["origin", "philosophy", "voice", "framework", "app", "boundaries", "personal"];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 10)         return "just now";
  if (s < 60)         return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m === 1)        return "1 min ago";
  if (m < 60)         return `${m} mins ago`;
  const h = Math.floor(m / 60);
  if (h === 1)        return "1 hour ago";
  if (h < 24)         return `${h} hours ago`;
  const days = Math.floor(h / 24);
  if (days === 1)     return "yesterday";
  if (days < 7)       return `${days} days ago`;
  return d.toLocaleDateString();
}

export default function FounderInterviewPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // { questionId: { answer, savedAt } }
  const [drafts, setDrafts] = useState({});   // unsaved edits
  const [savingId, setSavingId] = useState(null);
  const [activeCategory, setActiveCategory] = useState("origin");
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  // Tick — re-render once a minute so "saved 2 mins ago" stays fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [qRes, aRes] = await Promise.all([
          fetch(`${API}/api/excelior/interview/questions`, { headers: auth() }),
          fetch(`${API}/api/excelior/interview/answers`,  { headers: auth() }),
        ]);
        if (aRes.status === 403) {
          setAuthError(true);
          setLoading(false);
          return;
        }
        const qData = await qRes.json();
        const aData = await aRes.json();
        setQuestions(qData.questions || []);
        const map = {};
        (aData.answers || []).forEach((a) => { map[a.question_id] = a; });
        setAnswers(map);
        const draftMap = {};
        (aData.answers || []).forEach((a) => { draftMap[a.question_id] = a.answer || ""; });
        setDrafts(draftMap);
      } catch (e) {
        toast.error("Could not load the interview");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const saveAnswer = async (qid) => {
    const text = (drafts[qid] || "").trim();
    if (!text) {
      toast("Empty answer — write something first");
      return;
    }
    setSavingId(qid);
    try {
      const res = await fetch(`${API}/api/excelior/interview/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ question_id: qid, answer: text }),
      });
      if (!res.ok) throw new Error("save failed");
      const now = new Date().toISOString();
      setAnswers((prev) => ({ ...prev, [qid]: { answer: text, updated_at: now } }));
      toast.success("Saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSavingId(null);
    }
  };

  const clearAnswer = async (qid) => {
    if (!window.confirm("Clear this answer?")) return;
    try {
      await fetch(`${API}/api/excelior/interview/answer/${qid}`, {
        method: "DELETE",
        headers: auth(),
      });
      setAnswers((prev) => { const n = { ...prev }; delete n[qid]; return n; });
      setDrafts((prev) => ({ ...prev, [qid]: "" }));
    } catch {
      toast.error("Could not clear");
    }
  };

  const byCat = (cat) => questions.filter((q) => q.category === cat);
  const answeredInCat = (cat) =>
    byCat(cat).filter((q) => answers[q.id]?.answer).length;

  const totalAnswered = Object.values(answers).filter((a) => a.answer).length;
  const totalQuestions = questions.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0d12" }}>
        <Loader2 className="w-6 h-6 animate-spin text-white/50" />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center" style={{ background: "#0a0d12" }}>
        <p className="text-white/70 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
          The Founder Interview is private to Michael.
        </p>
        <button onClick={() => navigate("/")} className="text-white/40 underline">Go home</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#0a0d12", color: "rgba(245,240,230,0.9)" }} data-testid="founder-interview">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-6 text-white/40 hover:text-white/70"
          data-testid="founder-back"
        >
          <ArrowLeft size={18} /> Back
        </button>

        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] mb-2" style={{ color: "rgba(203,161,83,0.7)" }}>
              <Sparkles size={10} className="inline -mt-0.5 mr-1" /> Train Excelior
            </p>
            <h1 className="text-3xl mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
              Founder Interview
            </h1>
            <p className="text-white/45 text-sm max-w-lg" style={{ fontFamily: "'Spectral', serif" }}>
              Your answers below become Excelior's voice. The more you fill in, the more it sounds like you.
            </p>
            <p className="text-[11px] mt-2 max-w-lg" style={{ color: "rgba(203,161,83,0.7)", fontFamily: "'Manrope', sans-serif" }}>
              <Sparkles size={9} className="inline -mt-0.5 mr-1" />
              Tip — tap <span className="text-white/70">Speak</span> on any question to dictate your answer.
            </p>
          </div>
          <div className="text-right">
            <p className="text-[28px] leading-none" style={{ fontFamily: "'Playfair Display', serif", color: "#CBA153" }}>
              {totalAnswered}<span className="text-white/30 text-base"> / {totalQuestions}</span>
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 mt-1">answered</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-[2px] w-full bg-white/5 rounded-full overflow-hidden mt-4 mb-7">
          <motion.div
            className="h-full"
            style={{ background: "linear-gradient(90deg, #CBA153, #8B92F2)" }}
            initial={{ width: 0 }}
            animate={{ width: `${totalQuestions ? (totalAnswered / totalQuestions) * 100 : 0}%` }}
            transition={{ duration: 0.6 }}
          />
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto pb-3 -mx-6 px-6">
          {CATEGORY_ORDER.map((cat) => {
            const meta = CATEGORY_META[cat];
            const ans = answeredInCat(cat);
            const total = byCat(cat).length;
            const active = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="flex-shrink-0 rounded-full px-4 py-2 text-xs whitespace-nowrap transition-colors"
                style={{
                  background: active ? `${meta.tint}25` : "rgba(255,255,255,0.03)",
                  border: `1px solid ${active ? meta.tint + "55" : "rgba(255,255,255,0.06)"}`,
                  color: active ? meta.tint : "rgba(255,255,255,0.55)",
                  fontFamily: "'Manrope', sans-serif",
                  letterSpacing: "0.02em",
                }}
                data-testid={`founder-tab-${cat}`}
              >
                {meta.label}
                <span className="ml-2 opacity-60">{ans}/{total}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Questions */}
      <div className="px-6 pb-24 max-w-3xl mx-auto">
        <p className="text-[11px] uppercase tracking-[0.25em] text-white/35 mb-1">
          {CATEGORY_META[activeCategory]?.label}
        </p>
        <p className="text-white/40 text-sm italic mb-6" style={{ fontFamily: "'Spectral', serif" }}>
          {CATEGORY_META[activeCategory]?.hint}
        </p>

        <div className="space-y-4">
          {byCat(activeCategory).map((q) => {
            const draft = drafts[q.id] || "";
            const saved = answers[q.id]?.answer;
            const dirty = saved !== draft;
            const isSaving = savingId === q.id;

            return (
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-5"
                style={{
                  background: "rgba(255,255,255,0.025)",
                  border: `1px solid ${saved ? "rgba(203,161,83,0.18)" : "rgba(255,255,255,0.06)"}`,
                }}
                data-testid={`founder-q-${q.id}`}
              >
                <div className="flex items-start gap-2 mb-3">
                  <div className="flex-1">
                    <p className="text-white/85 text-[15px] leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {q.question}
                    </p>
                    {saved && answers[q.id]?.updated_at && !dirty && (
                      <p
                        className="text-[10px] mt-1.5 text-white/30"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                        data-testid={`founder-saved-at-${q.id}`}
                      >
                        saved {timeAgo(answers[q.id].updated_at)}
                      </p>
                    )}
                  </div>
                  {saved && !dirty && (
                    <span className="flex-shrink-0 mt-1 text-[10px] flex items-center gap-1" style={{ color: "rgba(203,161,83,0.85)" }}>
                      <Check size={12} /> SAVED
                    </span>
                  )}
                </div>

                <textarea
                  value={draft}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  placeholder="Speak it as if you were saying it to a client…"
                  rows={Math.max(3, Math.min(10, draft.split("\n").length + 1))}
                  className="w-full rounded-xl px-3 py-3 text-sm resize-none focus:outline-none"
                  style={{
                    background: "rgba(0,0,0,0.25)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    color: "rgba(245,240,230,0.92)",
                    fontFamily: "'Spectral', serif",
                    lineHeight: 1.55,
                  }}
                  data-testid={`founder-input-${q.id}`}
                />

                <div className="flex items-center justify-between mt-3">
                  <span className="text-[10px] text-white/30">
                    {draft.length > 0 ? `${draft.length} chars` : ""}
                  </span>
                  <div className="flex items-center gap-2">
                    <SpeakAnswerButton
                      questionId={q.id}
                      onTranscript={(text) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [q.id]: prev[q.id] ? `${prev[q.id]} ${text}`.trim() : text,
                        }))
                      }
                    />
                    {saved && (
                      <button
                        onClick={() => clearAnswer(q.id)}
                        className="text-white/35 hover:text-white/60 p-2"
                        title="Clear"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => saveAnswer(q.id)}
                      disabled={!dirty || isSaving}
                      className="rounded-full px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-30"
                      style={{
                        background: dirty ? "rgba(203,161,83,0.15)" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${dirty ? "rgba(203,161,83,0.4)" : "rgba(255,255,255,0.06)"}`,
                        color: dirty ? "#F5EBC4" : "rgba(255,255,255,0.4)",
                      }}
                      data-testid={`founder-save-${q.id}`}
                    >
                      {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      {dirty ? (isSaving ? "Saving…" : "Save") : "Saved"}
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/**
 * SpeakAnswerButton — record audio in the browser, send to /api/excelior/interview/transcribe,
 * append the transcript to the answer draft. Lets Michael speak instead of type.
 */
function SpeakAnswerButton({ questionId, onTranscript }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTimeRef = useRef(0);
  const [elapsed, setElapsed] = useState(0);
  const elapsedTimerRef = useRef(null);

  const stop = async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    setRecording(false);
  };

  const start = async () => {
    if (recording || transcribing) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Recording isn’t supported on this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Pick the most compatible mime type the browser supports
      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/wav",
      ];
      const mimeType =
        mimeCandidates.find((m) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) ||
        "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        if (blob.size < 500) {
          toast("Too short — hold the mic a beat longer");
          return;
        }
        setTranscribing(true);
        try {
          const ext = (blob.type.includes("mp4") ? "mp4" : blob.type.includes("wav") ? "wav" : "webm");
          const file = new File([blob], `answer-${questionId}.${ext}`, { type: blob.type });
          const fd = new FormData();
          fd.append("audio", file);

          const token = localStorage.getItem("reclaim_token");
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/excelior/interview/transcribe`,
            {
              method: "POST",
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              body: fd,
            }
          );
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err?.detail || "Transcription failed");
          }
          const data = await res.json();
          if (data.text && data.text.trim()) {
            onTranscript(data.text.trim());
            toast.success("Heard you");
          } else {
            toast("Nothing transcribed — try again");
          }
        } catch (err) {
          toast.error(String(err?.message || err));
        } finally {
          setTranscribing(false);
          setElapsed(0);
        }
      };

      recorder.start();
      setRecording(true);
      startTimeRef.current = Date.now();
      elapsedTimerRef.current = setInterval(
        () => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)),
        500
      );
    } catch (err) {
      console.error("[FounderInterview] mic error", err);
      setPermissionBlocked(true);
      toast.error("Microphone blocked. Allow access in browser settings.");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (permissionBlocked) {
    return (
      <button
        onClick={() => setPermissionBlocked(false)}
        className="text-white/35 hover:text-white/60 p-2"
        title="Mic blocked — tap to retry"
        data-testid={`founder-mic-blocked-${questionId}`}
      >
        <Mic size={14} />
      </button>
    );
  }

  if (transcribing) {
    return (
      <span
        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px]"
        style={{ background: "rgba(203,161,83,0.10)", color: "rgba(245,235,196,0.85)", border: "1px solid rgba(203,161,83,0.3)" }}
        data-testid={`founder-transcribing-${questionId}`}
      >
        <Loader2 size={12} className="animate-spin" /> Transcribing…
      </span>
    );
  }

  if (recording) {
    return (
      <button
        onClick={stop}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px]"
        style={{ background: "rgba(220,90,90,0.18)", color: "#F9C5C5", border: "1px solid rgba(220,90,90,0.45)" }}
        data-testid={`founder-stop-${questionId}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
        <Square size={11} />
        {elapsed}s
      </button>
    );
  }

  return (
    <button
      onClick={start}
      className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px]"
      style={{ background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.10)" }}
      title="Speak your answer"
      data-testid={`founder-mic-${questionId}`}
    >
      <Mic size={12} /> Speak
    </button>
  );
}
